/**
 * Student Dashboard Page — ThinkOtter design (redesigned 2026-06 to align with the
 * parent dashboard: warm .otter-bg, Fredoka/Nunito fonts, identity chips, an equal-height
 * hero row, and a "This week" stat strip).
 *
 * Frontend-only — every value comes from the existing GET /users/me/dashboard
 * (useDashboard), /users/me (usePebbles), and /sessions (useSessions). No backend change.
 * The hardcoded-dummy week.bestWeekFirstTryWins is intentionally NOT shown; the strip uses
 * a real "Days practised" metric derived from dailyPebbles instead.
 *
 * Preserved behaviour/contracts (US-004/US-015/US-018/US-020/US-021):
 * - h1 "Dashboard" (sr-only) for the page heading contract; visible greeting is the h2.
 * - Weekly training copy: "You trained {n} times this week!" / "Start your first session this week!"
 * - WeeklyProgressBar (progressbar role + "X of Y sessions this week").
 * - CTA aria-labels: "Resume your session" / "Go to my sessions".
 *   (Math Coach entry point removed — POC-only feature, not exposed to students.)
 * - Neutral "0 sessions remaining" state when out of sessions and no active session
 *   (CCM-03: no selling, no "ask a grown-up", no purchase UI — sessions-framed only).
 * - Recent sessions + "See all my sessions"; LastReportHighlightCard when lastReport present.
 *
 * Extended by show-not-tell/TS-003 (T5, AC-TS003.2/.3/.4): the hero's right slot
 * conditionally swaps to WritingPracticeHeroCard (1fr/1fr grid, left eyebrow
 * relabelled "Reading comprehension") when dashboard.features.show_not_tell is
 * true; absent/false renders the existing hero byte-for-byte unchanged. The
 * top-bar pebble chip and PebblesProgressCard trend card are untouched and
 * remain visible in BOTH states (BR/DD-5). Enforces TO-LL-003 (features? is
 * optional, absent treated as off via `?? false`) and TO-LL-026 (no new
 * unconditional invalidateQueries added by this swap).
 */

import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboard';
import { usePebbles } from '@/hooks/usePebbles';
import { useSessions } from '@/hooks/useSessions';
import { AppLogo } from '@/components/AppLogo';
import { StudentProfileHeader } from '@/components/StudentProfileHeader';
import { PebblesDisplay } from '@/components/PebblesDisplay';
import { ProfileIcon } from '@/components/ProfileIcon';
import { PebblesProgressCard } from '@/components/PebblesProgressCard';
import { PebblesHeroCard } from '@/components/PebblesHeroCard';
import { WritingPracticeHeroCard } from '@/components/WritingPracticeHeroCard';
import { WritingPracticeTile } from '@/components/WritingPracticeTile';
import { StudentWeeklyStrip } from '@/components/StudentWeeklyStrip';
import { SuperpowersCard } from '@/components/SuperpowersCard';
import { NextLevelTrainingCard } from '@/components/NextLevelTrainingCard';
import { WeeklyProgressBar } from '@/components/WeeklyProgressBar';
import { LogoutButton } from '@/components/LogoutButton';
import { StarRating } from '@/components/StarRating';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { LastReportHighlightCard } from '@/components/LastReportHighlightCard';
import { formatRelativeTime, truncateText } from '@/lib/formatters';

export function StudentDashboardPage() {
  const { dashboard, isLoading, error, refetch } = useDashboard();
  const recentSessions = dashboard?.recentSessions ?? [];
  const lastReport = dashboard?.lastReport ?? null;
  // show-not-tell/TS-003 (T5, AC-TS003.3): absent/false features map = off.
  // Enforces TO-LL-003 — optional chaining with a falsy default so undefined
  // (older responses / degrade) never throws and always renders the
  // unchanged hero.
  const showNotTell = dashboard?.features?.show_not_tell ?? false;
  const { totalPebbles, profile, isLoading: pebblesLoading } = usePebbles();
  // US-015 T3: active-session state from useSessions (excludes 'cancelled' per CCM-01).
  const { activeSessions } = useSessions();
  const hasActiveSession = activeSessions.length > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // US-021 T5: sticky out-of-coins flag set by useStartSession after a 402.
  const { data: stickyOutOfCoins } = useQuery<boolean>({
    queryKey: ['family', 'outOfCoins'],
    queryFn: () => false,
    staleTime: Infinity,
  });
  const outOfCoins = stickyOutOfCoins === true;

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* ── Page header ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <AppLogo />
        <div className="flex items-center gap-2 md:gap-3">
          {/* Pebble chip */}
          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 shadow-sm">
            {pebblesLoading ? (
              <span className="text-base text-gray-400">-</span>
            ) : (
              <PebblesDisplay totalPebbles={totalPebbles} size="sm" />
            )}
          </span>
          {/* Identity chip — profile (name hidden on small screens by StudentProfileHeader) */}
          <span className="inline-flex items-center rounded-full border border-otter-sand bg-white px-2 py-1 shadow-sm">
            <StudentProfileHeader
              displayName={profile?.displayName || ''}
              profileImageUrl={profile?.profileImageUrl}
              onUploadSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
                queryClient.invalidateQueries({ queryKey: ['user', 'dashboard'] });
              }}
            />
          </span>
          <LogoutButton />
        </div>
      </header>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-6xl">
        {/* Page heading contract (sr-only); the visible greeting is the h2 below. */}
        <h1 className="sr-only">Dashboard</h1>

        {/* Loading state */}
        {isLoading && <LoadingState message="Loading your dashboard..." />}

        {/* Error state */}
        {error && !isLoading && (
          <ErrorState message="Can't load progress. Tap to retry." onRetry={refetch} />
        )}

        {/* Dashboard content */}
        {!isLoading && !error && dashboard && (
          <div className="px-4 pb-8 pt-4 md:px-6 md:pt-7">
            {/* Greeting */}
            <div className="mb-5">
              <h2 className="font-display text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
                Welcome back, {dashboard.user.displayName}! <span aria-hidden="true">👋</span>
              </h2>
              <p className="mt-1 text-sm font-medium text-gray-500 md:text-[15px]">
                Here&apos;s your training progress — keep up the great work!
              </p>
            </div>

            {/* ── Hero row: action card + right-slot card (equal height) ─────
                show-not-tell/TS-003 (T5, AC-TS003.2/.3): grid ratio is
                conditional on showNotTell — 1fr/1fr (grid-cols-2) when the
                flag is on, else the original 1.5fr/1fr ratio (unchanged). */}
            <div
              className={`mb-5 grid gap-5 lg:items-stretch md:mb-6 ${
                showNotTell ? 'lg:grid-cols-2' : 'lg:grid-cols-[1.5fr_1fr]'
              }`}
            >
              {/* Action card — eyebrow relabelled "Reading comprehension" when
                  showNotTell is true (AC-TS003.2); "Ready to train" unchanged
                  otherwise (AC-TS003.3). */}
              <div className="relative flex flex-col overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-otter-orange-400/20 blur-2xl"
                />
                <p className="relative z-[1] mb-4 text-[11px] font-extrabold uppercase tracking-wider text-otter-orange-600">
                  <span aria-hidden="true">🦦</span>{' '}
                  {showNotTell ? 'Reading comprehension' : 'Ready to train'}
                </p>
                <div className="relative z-[1] flex items-center gap-4">
                  <ProfileIcon
                    imageUrl={profile?.profileImageUrl}
                    size="lg"
                    className="border-4 border-white shadow-md"
                  />
                  <div className="min-w-0">
                    {/* Weekly training count — copy preserved exactly for tests (US-004) */}
                    {dashboard.week.sessionsThisWeek > 0 ? (
                      <p className="font-display text-xl font-semibold leading-tight text-otter-earth-800">
                        You trained{' '}
                        <span className="text-otter-orange-600">
                          {dashboard.week.sessionsThisWeek}
                        </span>{' '}
                        times this week!
                      </p>
                    ) : (
                      <p className="font-display text-xl font-semibold leading-tight text-otter-earth-800">
                        Start your first session this week!
                      </p>
                    )}
                    <p className="mt-1 text-[13px] font-semibold text-orange-700/80">
                      Let&apos;s pick up where you left off.
                    </p>
                  </div>
                </div>

                {/* CTA block (US-015 / US-021) — resume / go / out-of-sessions */}
                <div className="relative z-[1] mt-auto grid gap-3 pt-5">
                  {outOfCoins && !hasActiveSession ? (
                    // Child-friendly "0 sessions remaining" that nudges telling a parent
                    // (no in-app selling). Supersedes the earlier neutral CCM-03 wording.
                    <div
                      className="rounded-2xl border border-otter-sand bg-white/70 py-6 text-center"
                      role="status"
                    >
                      <p className="font-display text-lg font-semibold text-otter-earth-800">
                        <span aria-hidden="true">🎟️</span> 0 sessions remaining
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        You&apos;ve finished all your sessions for now. Ask a parent to unlock more.
                      </p>
                    </div>
                  ) : hasActiveSession ? (
                    <>
                      <Button
                        variant="warm"
                        size="lg"
                        onClick={() => navigate(`/session/${activeSessions[0].sessionId}`)}
                        className="h-12"
                        aria-label="Resume your session"
                      >
                        Resume your session
                      </Button>
                      <p
                        className="text-sm text-otter-earth-700"
                        role="status"
                        aria-live="polite"
                      >
                        Finish this session before you can start a new one
                      </p>
                    </>
                  ) : (
                    <Button
                      variant="warm"
                      size="lg"
                      onClick={() => navigate('/sessions')}
                      className="h-12"
                      aria-label="Go to my sessions"
                    >
                      Go to my sessions
                    </Button>
                  )}

                  {/* "Share Feedback" moved to the parent dashboard (a grown-up action).
                      Math Coach entry point removed from the student dashboard — that
                      feature is POC-only and not exposed to students yet. */}
                </div>
              </div>

              {/* Right slot: show-not-tell/TS-003 (T5, AC-TS003.2/.3) — Writing
                  practice doorway when showNotTell is true; the original
                  pebbles "wallet" card otherwise (unchanged). The top-bar
                  pebble chip (above) and PebblesProgressCard (below) are
                  untouched in both states, so the pebble total remains
                  visible regardless of the flag (AC-TS003.4). */}
              {showNotTell ? (
                <WritingPracticeHeroCard />
              ) : (
                <PebblesHeroCard
                  totalPebbles={dashboard.user.totalPebbles}
                  dailyPebbles={dashboard.dailyPebbles}
                />
              )}
            </div>

            {/* ── "This week" strip ─────────────────────────────────────────── */}
            <StudentWeeklyStrip week={dashboard.week} dailyPebbles={dashboard.dailyPebbles} />

            {/* ── Weekly goal progress (progressbar contract preserved) ─────── */}
            <div className="mb-4 rounded-2xl border border-otter-sand bg-white p-4 shadow-sm md:mb-6">
              <WeeklyProgressBar
                current={dashboard?.week?.weeklyGoalCurrent}
                target={dashboard?.week?.weeklyGoalTarget}
              />
            </div>

            {/* ── Last report highlight (only when present) ─────────────────── */}
            {lastReport && <LastReportHighlightCard lastReport={lastReport} />}

            {/* ── Superpowers + Next-level training ─────────────────────────── */}
            <div className="mb-4 grid grid-cols-1 gap-4 md:mb-6 md:grid-cols-2">
              <SuperpowersCard strengths={dashboard.strengths} />
              <NextLevelTrainingCard improvements={dashboard.improvements} />
            </div>

            {/* ── Pebbles 7-day chart ───────────────────────────────────────── */}
            <div className="mb-4 md:mb-6">
              <PebblesProgressCard dailyPebbles={dashboard.dailyPebbles} />
            </div>

            {/* show-not-tell/TS-012 (T5, AC-TS012.1/.3): additive writing-
                practice recent-activity tile, gated on the SAME
                features.show_not_tell entitlement flag the hero swap uses
                (TS-003). Non-piloted students never render this tile and
                never call GET /recent (which would 404 at Gate B). */}
            {showNotTell && <WritingPracticeTile />}

            {/* ── Recent sessions ───────────────────────────────────────────── */}
            <section aria-label="Recent sessions">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-otter-earth-800">
                  Recent sessions
                </h2>
                <Link
                  to="/sessions"
                  className="text-sm font-bold text-otter-orange-600 hover:text-otter-orange-500"
                >
                  See all my sessions
                </Link>
              </div>

              {recentSessions.length === 0 ? (
                <EmptyState message="No sessions yet — start your first one!" />
              ) : (
                <ul className="space-y-2.5" role="list">
                  {recentSessions.map((row) => (
                    <li key={row.sessionId}>
                      <button
                        type="button"
                        onClick={() => navigate(`/session/${row.sessionId}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/session/${row.sessionId}`);
                          }
                        }}
                        className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-2xl border border-otter-sand bg-white px-4 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
                        aria-label={`Open session: ${row.passageTitle || 'Reading session'}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-otter-earth-800">
                            {truncateText(row.passageTitle || 'Reading session', 60)}
                          </p>
                          <p className="mt-0.5 text-xs font-medium text-gray-500">
                            {row.date ? formatRelativeTime(row.date) : ''}
                          </p>
                        </div>
                        <StarRating rating={row.starRating} size="sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
